import text from './text.json'
import data from './data.json'
import {Input} from '../../components/core/input'
import Textarea from '../../components/core/textarea'
import {SimpleBlueButton} from '../../components/core/button'
import ButtonLoader from '../../components/core/button/ButtonLoader'
import {Formik} from 'formik'
import {useState, useEffect, useRef} from 'react'
import CustomSelect from '../../components/core/customSelect'
import Checkbox from '../../components/core/checkbox'
import {incidentSchema} from '../../utils/validation'
import {createIncident, updateIncident, getIncidentActions} from '../../stores/incident-management'
import {useDispatch, useSelector} from 'react-redux'
import {isNewFeatureEnabled} from '../../utils/newFeature'
import {HelpIcon} from '../../icons'
import InputInfo from '../../components/core/inputInfo'
import useOnClickOutside from '../../utils/hooks/useOutsideClick'
import classNames from 'classnames'
import {trimStringValues} from '../../utils/text'
import localStorageKeys from '../../utils/localStorageKeys'
import plans from '../../data/plans.json'

const statusOptions = Object.values(data.status_options)
const CreateEditIncident = ({isEdit, incident, closeModal, setCheckedIncidentsIds, setSelectedTab, allMembers, allComponents}) => {
    const dispatch = useDispatch()
    const teamId = useSelector(state => state.teams.selectedTeamId)
    const teams = useSelector(state => state.teams.teams)
    const isProcessing = useSelector(state => state.incident_management.isProcessing)
    const incidentActions = useSelector(state => state.incident_management.incidentActions)
    const isDarkMode = useSelector(state => state.sidebar.isDarkMode)
    const plan = localStorage.getItem(localStorageKeys.PLAN)
    const [active, setActive] = useState(null)

    const actionsInfoRef = useRef(null)
    useOnClickOutside(actionsInfoRef, () => setActive(active === names.actions && ''))

    const names = {
        name: 'name',
        description: 'description',
        status: 'status',
        urgency: 'urgency',
        actions: 'actions',
        components: 'components',
        team_assignees: 'team_assignees',
        member_assignees: 'member_assignees'
    }

    const handleInfo = (index, state) => {
        state ? setActive(index) : setActive(null)
    }

    const teamAssigneesOptions = teams?.map(assignee => {
        return {
            label: assignee?.label,
            value: assignee?.value,
            type: 'team'
        }
    })

    const membersAssigneesOptions = allMembers?.map(assignee => {
        return {
            label: assignee?.label,
            value: assignee?.value,
            type: 'customer'
        }
    })

    const initialFormValues = {
        name: isEdit ? incident?.name : '',
        description: isEdit ? incident?.description : '',
        status: isEdit ? statusOptions?.find(option => option?.value === incident?.status) : statusOptions?.[0],
        urgency: isEdit ? data?.urgency_options?.find(urgency => urgency?.value === incident?.urgency) : data?.urgency_options?.[0],
        components:  isEdit ? incidentActions?.length > 0 ?
            incidentActions?.filter(action => action?.type === data?.action_types_options?.trigger_statuspage_update)?.map(action => {
                return action?.settings?.map(setting => {
                    return allComponents?.find(component => component?.value === setting?.target?.id)
                })
            })?.reduce((accumulator, currentArray) => {
                return accumulator.concat(currentArray)
            }, []) : [] : [],
        team_assignees: isEdit ? incident?.assignees?.length > 0 ? incident?.assignees?.filter(item => item?.type === 'team')?.map(asignee => {
            return teamAssigneesOptions?.find(option => option?.value === asignee?.id)
        }) : [] : [teamAssigneesOptions?.find(option => option?.value === teamId)],
        member_assignees: isEdit ? incident?.assignees?.length > 0 ? incident?.assignees?.filter(item => item?.type === 'customer')?.map(asignee => {
            return membersAssigneesOptions?.find(option => option?.value === asignee?.id)
        }) : [] : [],
        actions: isEdit ?
            incidentActions?.length > 0 ?
                incidentActions?.map(action => action?.type) :
                [] :
            [data?.action_types_options?.trigger_alerts]
    }

    const createEditIncident = (values, {setFieldError}) => {
        const teamAssignees = values?.team_assignees
        const memberAssignees = values?.member_assignees
        const filteredActions = values?.components?.length > 0 ?
            values.actions :
            values.actions?.filter(type => type === data?.action_types_options?.trigger_alerts)
        const allAssignees = (teamAssignees && memberAssignees) ? [...teamAssignees, ...memberAssignees] : (teamAssignees || memberAssignees || null)
        const payload = {
            name: values?.name,
            description: values?.description,
            status: values?.status?.value,
            urgency: values?.urgency?.value,
            actions: filteredActions?.map(type => {
                return type === data?.action_types_options?.trigger_alerts ?
                    {
                        type: type
                    } :
                    {
                        type: type,
                        settings: values?.components?.map(component => {
                            return {
                                status_page_id: component?.statuspage_id,
                                component_id: component?.value
                            }
                        })
                    }
            }),
            assignees: allAssignees?.map(assignee => {
                return {
                    id: assignee?.value,
                    type: assignee?.type
                }
            })
        }
        isEdit ?
            dispatch(updateIncident(incident?.id, trimStringValues(payload), setCheckedIncidentsIds, teamId, setFieldError, closeModal)) :
            dispatch(createIncident(teamId, trimStringValues(payload), setFieldError, closeModal, setCheckedIncidentsIds))
        setSelectedTab('all')
    }

    const handleActionChange = (actions, action, setFieldValue) => {
        actions?.includes(action) ?
            setFieldValue(names.actions, actions?.filter(type => type !== action)) :
            setFieldValue(names.actions, actions.concat(action))
    }

    useEffect(() => {
        isEdit && dispatch(getIncidentActions(incident?.id))
    }, [])

    const isDisabled = plan === plans.free_v2.key

    return (
        <>
            <Formik initialValues={initialFormValues}
                enableReinitialize={true}
                onSubmit={createEditIncident}
                validationSchema={incidentSchema}
                validateOnBlur={false}
                validateOnChange={false}>
                {({errors, handleSubmit, setFieldValue, handleChange, values}) => (
                    <form onSubmit={handleSubmit} className="modal-body">
                        <Input
                            name={names.name}
                            value={values.name}
                            error={errors.name}
                            placeholder={text.name_placeholder}
                            infoText={text.name_info}
                            inputIsDisabled={isDisabled}
                            onChange={handleChange}
                            activeInfo={active === names.name}
                            handleInfo={(state) => handleInfo(names.name, state)}
                        />
                        <Textarea
                            name={names.description}
                            isDisabled={isDisabled}
                            value={values.description}
                            error={errors.description}
                            placeholder={text.description_placeholder}
                            infoText={text.description_info}
                            onChange={handleChange}
                            activeInfo={active === names.description}
                            handleInfo={(state) => handleInfo(names.description, state)}
                        />
                        {isEdit &&
                        <CustomSelect
                            name={names.status}
                            value={values.status}
                            onChange={setFieldValue}
                            placeholder={text.status_placeholder}
                            infoText={text.status_info}
                            options={statusOptions}
                            activeInfo={active === names.status}
                            handleInfo={(state) => handleInfo(names.status, state)}
                            error={errors.status}
                        />
                        }
                        <CustomSelect
                            name={names.urgency}
                            value={values.urgency}
                            onChange={setFieldValue}
                            isDisabled={isDisabled}
                            placeholder={text.urgency_placeholder}
                            infoText={text.urgency_info}
                            options={data.urgency_options}
                            activeInfo={active === names.urgency}
                            handleInfo={(state) => handleInfo(names.urgency, state)}
                            error={errors.urgency}
                        />
                        <CustomSelect
                            name={names.team_assignees}
                            value={values.team_assignees}
                            isDisabled={isDisabled}
                            onChange={setFieldValue}
                            placeholder={text.team_assignees_placeholder}
                            infoText={text.team_assignees_info}
                            options={teamAssigneesOptions}
                            activeInfo={active === names.team_assignees}
                            handleInfo={(state) => handleInfo(names.team_assignees, state)}
                            error={errors.team_assignees}
                            isMulti
                        />
                        {isNewFeatureEnabled() && (
                            <CustomSelect
                                name={names.member_assignees}
                                value={values.member_assignees}
                                onChange={setFieldValue}
                                isDisabled={isDisabled}
                                placeholder={text.member_assignees_placeholder}
                                infoText={text.member_assignees_info}
                                options={membersAssigneesOptions}
                                activeInfo={active === names.member_assignees}
                                handleInfo={(state) => handleInfo(names.member_assignees, state)}
                                error={errors.member_assignees}
                                isMulti
                            />
                        )}
                        <div className={classNames('actions', {
                            dark: isDarkMode
                        })}>
                            <div className="actions-header">
                                <h4>{text.action_type_placeholder}</h4>
                                <div
                                    ref={actionsInfoRef}
                                    className={`help ${active === names.actions ? 'active' : ''}`}
                                    onClick={() => setActive(active === names.actions ? '' : names.actions)}
                                >
                                    <span><HelpIcon/></span>
                                    <InputInfo text={text.action_type_info}/>
                                </div>
                            </div>
                            <Checkbox
                                label={text.trigger_alerts}
                                onChange={() => handleActionChange(values.actions, data.action_types_options.trigger_alerts, setFieldValue)}
                                type="checkbox"
                                disabled={isDisabled}
                                checked={values.actions?.includes(data?.action_types_options?.trigger_alerts)}
                            />
                            <Checkbox
                                label={text.trigger_statuspage_update}
                                onChange={() => handleActionChange(values.actions, data.action_types_options.trigger_statuspage_update, setFieldValue)}
                                disabled={isDisabled}
                                type="checkbox"
                                checked={values.actions?.includes(data?.action_types_options?.trigger_statuspage_update)}
                            />
                        </div>
                        {values.actions?.includes(data?.action_types_options?.trigger_statuspage_update) &&
                        <>
                            <CustomSelect
                                name={names.components}
                                value={values.components}
                                options={allComponents}
                                placeholder={text.components_placeholder}
                                infoText={text.components_info}
                                activeInfo={active === names.components}
                                handleInfo={(state) => handleInfo(names.components, state)}
                                onChange={setFieldValue}
                                error={errors.components}
                                isMulti
                            />
                        </>
                        }
                        <SimpleBlueButton
                            type="submit"
                            disabled={isProcessing || isDisabled}
                        >
                            {isProcessing && <ButtonLoader/>}
                            {text.submit}
                        </SimpleBlueButton>
                    </form>
                )}
            </Formik>
        </>
    )
}

export default CreateEditIncident
